import React, { useState } from "react";
import {
    BoxCard,
    ConfirmationDialog,
    ConfirmationDialogState,
    FormButton,
    SelectUsers,
} from "../../componentes";
import {
    Button,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@material-ui/core";
import { User, UserProfile, useUsers } from "../../../api";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { functions } from "../../../config/firebaseConfig";
import { useSnackbar } from "notistack";
import { LOGIN_ERROR_MESSAGES } from "../../../redux/reducers/authReducer";

const clearConfirmation = (setConfirmation) => () =>
    setConfirmation({ message: "", showDialog: false });

export const UsersAdmin = () => {
    const [confirmation, setConfirmation] = useState<ConfirmationDialogState>({
        showDialog: false,
        message: "",
    });
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState<UserProfile>();
    const [showPassword, setShowPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [changingEmail, setChangingEmail] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const userToUpdate = useUsers<User>({
        id: user?.id || "-1",
        withPrivateInfo: true,
        withRoles: true,
    });

    return (
        <BoxCard minHeight="400px">
            <ConfirmationDialog
                title={confirmation.title}
                showDialog={confirmation.showDialog}
                message={confirmation.message}
                onCancel={clearConfirmation(setConfirmation)}
                onOk={confirmation.onOk}
            />
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Button onClick={() => setOpen(true)} color="primary">
                        Selecionar usuario
                    </Button>
                    <SelectUsers
                        open={open}
                        onSelectUser={(driver) => {
                            setUser(driver);
                            setOpen(false);
                        }}
                        onClose={() => setOpen(false)}
                    />
                </Grid>
                <Grid item xs={12} hidden={!!user}>
                    <Typography variant="h5" gutterBottom>
                        Selecione um usuário para editar sua senha ou email
                    </Typography>
                </Grid>
                <Grid item xs={12} hidden={!user}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>
                                {`${userToUpdate?.displayName} - ${userToUpdate?.privateInfo?.email}`}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="newPassword"
                                name="newPassword"
                                label="Nova senha"
                                value={newPassword}
                                onChange={(v) => setNewPassword(v.target.value)}
                                type={showPassword ? "text" : "password"}
                                inputProps={{ autoComplete: "new-password" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                                onMouseDown={(e) =>
                                                    e.preventDefault()
                                                }
                                            >
                                                {showPassword ? (
                                                    <Visibility />
                                                ) : (
                                                    <VisibilityOff />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormButton
                                loading={changingPassword}
                                style={{ marginTop: 8 }}
                                color="secondary"
                                onClick={() => {
                                    setConfirmation({
                                        title: "Alteração de senha!",
                                        message: `Deseja alterar a senha para o usuário ${userToUpdate.displayName} - ${userToUpdate.privateInfo.email}?`,
                                        showDialog: true,
                                        onOk: () => {
                                            setChangingPassword(true);
                                            clearConfirmation(
                                                setConfirmation
                                            )();
                                            functions
                                                .httpsCallable(
                                                    "user-updateUserPassword"
                                                )({
                                                    userId: user?.id,
                                                    newPassword,
                                                })
                                                .then(() => {
                                                    enqueueSnackbar(
                                                        "Senha alterada com sucesso!",
                                                        {
                                                            variant: "success",
                                                        }
                                                    );
                                                })
                                                .catch(() => {
                                                    enqueueSnackbar(
                                                        "Não foi possível atualizar a senha!",
                                                        {
                                                            variant: "error",
                                                        }
                                                    );
                                                })
                                                .finally(() => {
                                                    setChangingPassword(false);
                                                });
                                        },
                                    });
                                }}
                            >
                                {changingPassword
                                    ? "Alterando senha"
                                    : `Alterar senha`}
                            </FormButton>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={6}>
                            <TextField
                                id="newEmail"
                                name="newEmail"
                                label="Novo email"
                                inputProps={{ autoComplete: "off" }}
                                value={newEmail}
                                onChange={(v) => setNewEmail(v.target.value)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormButton
                                loading={changingEmail}
                                style={{ marginTop: 8 }}
                                color="secondary"
                                onClick={() => {
                                    setConfirmation({
                                        title: "Alteração de email!",
                                        message: `Deseja alterar o email do usuário ${userToUpdate.displayName} de ${userToUpdate.privateInfo.email} para ${newEmail}?`,
                                        showDialog: true,
                                        onOk: () => {
                                            setChangingEmail(true);
                                            clearConfirmation(
                                                setConfirmation
                                            )();
                                            functions
                                                .httpsCallable(
                                                    "user-updateUserEmail"
                                                )({
                                                    userId: user?.id,
                                                    newEmail,
                                                })
                                                .then((updated) => {
                                                    if (updated) {
                                                        enqueueSnackbar(
                                                            "Email alterado com sucesso!",
                                                            {
                                                                variant:
                                                                    "success",
                                                            }
                                                        );
                                                    } else {
                                                        enqueueSnackbar(
                                                            "Não foi possível alterar o email do usuário!",
                                                            {
                                                                variant:
                                                                    "error",
                                                            }
                                                        );
                                                    }
                                                })
                                                .catch(() => {
                                                    enqueueSnackbar(
                                                        LOGIN_ERROR_MESSAGES[
                                                            "auth/invalid-email"
                                                        ],
                                                        {
                                                            variant: "error",
                                                        }
                                                    );
                                                })
                                                .finally(() => {
                                                    setChangingEmail(false);
                                                });
                                        },
                                    });
                                }}
                            >
                                {changingEmail
                                    ? "Alterando email"
                                    : "Alterar email"}
                            </FormButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </BoxCard>
    );
};
